import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, min, subWeeks } from 'date-fns';
import { useEffect } from 'react';

import { ALIGNMENTS, DateRangeSelector } from '@/components';
import { EVENT_CONSTANTS } from '@/services';
import {
  memberReportingActions,
  selectReportDateRange,
  useAppDispatch,
  useAppSelector,
  useFetchReportingConfigQuery,
} from '@/store';

import {
  AcquiredMembers,
  ActiveMembers,
  LearningTrend,
  ListenerDistribution,
  TopEpisodes,
  TopListeners,
  TotalEpisodesListened,
} from './components';
import { styles } from './styles';

export const Reporting = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dateRange = useAppSelector(selectReportDateRange);
  const { data: reportingConfig } = useFetchReportingConfigQuery();

  useEffect(() => {
    const fromDate = subWeeks(new Date(), 4);
    const toDate = new Date();

    dispatch(
      memberReportingActions.setDateRange({
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      }),
    );
  }, [dispatch]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          mb: 3,
          gap: 2,
        }}>
        <Typography variant="h1">Reporting</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {reportingConfig && !isMobile && (
            <Tooltip
              title={`Data for your organization only exists from ${format(new Date(reportingConfig.minDate), 'MMM d, yyyy')} to ${format(new Date(reportingConfig.maxDate), 'MMM d, yyyy')}`}
              arrow
              placement="bottom-start">
              <InfoOutlinedIcon
                sx={{
                  color: 'text.trace',
                  fontSize: 16,
                  mt: 0.5,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {reportingConfig && dateRange.fromDate && dateRange.toDate && (
            <DateRangeSelector
              fromDate={new Date(dateRange.fromDate)}
              toDate={new Date(dateRange.toDate)}
              onDateChange={([newFromDate, newToDate]) => {
                dispatch(
                  memberReportingActions.setDateRange({
                    fromDate: newFromDate.toISOString(),
                    toDate: newToDate.toISOString(),
                  }),
                );
              }}
              limits={{
                // allow selection as far back as 4 weeks ago, even if no data exists for some of that period
                // but beyond 4 weeks, respect the reporting config because some early data may not be reliable
                min: min([
                  subWeeks(new Date(), 4),
                  ...(reportingConfig?.minDate
                    ? [reportingConfig.minDate]
                    : []),
                ]),
              }}
              alignment={ALIGNMENTS.RIGHT}
              referrer={{ page: EVENT_CONSTANTS.PAGE.REPORTING }}
            />
          )}
        </Box>
      </Box>
      <Box sx={styles.column}>
        <Box sx={styles.row}>
          <AcquiredMembers />
          <ActiveMembers />
          <TotalEpisodesListened />
        </Box>
        <Box sx={styles.row}>
          <ListenerDistribution />
          <LearningTrend />
        </Box>
        <Box sx={styles.row}>
          <TopListeners />
          <TopEpisodes />
        </Box>
      </Box>
    </Box>
  );
};
